import {CommonModule} from '@angular/common';
import {Component, inject} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {IBasePageComponent} from '../../../../sdk/src/components/ibase-page/ibase-page.component';
import {CpmCServiceRequestApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-service-request.api-model';
import {ServiceRequestGeneralInfoComponent} from '../../components/service-request/service-request-general-info/service-request-general-info.component';
import {ServiceRequestTasksComponent} from '../../components/service-request/service-request-tasks/service-request-tasks.component';
import {UserFileCRUDComponent} from '../../components/user-file-crud/user-file-crud.component';
import {ObjectStoreEntryCRUDComponent} from '../../components/object-store-entry-crud/object-store-entry-crud.component';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {NewEditLocationModalComponent} from '../../modals/new-edit-location-modal/new-edit-location-modal.component';

/**
 * Enum representing available tabs in the service request overview page
 */
export enum EServiceRequestTab {
    GENERAL = 'general',
    TASKS = 'tasks',
    FILES = 'files',
    OBJECT_STORE = 'object-store'
}

/**
 * Customer Service Request Overview Page Component
 *
 * Displays a tabbed interface for viewing and managing service request details including:
 * - General information
 * - Tasks
 * - Files
 * - Object store entries
 *
 * The active tab is synchronized with URL query parameters for bookmarkability and navigation.
 */
@Component({
    selector: 'app-csr-overview-page',
    templateUrl: './csr-overview-page.component.html',
    imports: [
        CommonModule,
        PrimeNgImportsModule,
        ServiceRequestGeneralInfoComponent,
        ServiceRequestTasksComponent,
        UserFileCRUDComponent,
        ObjectStoreEntryCRUDComponent
    ]
})
export class CSROverviewPageComponent extends IBasePageComponent {
    protected readonly EServiceRequestTab = EServiceRequestTab;

    // ===============================================================
    // ===============================================================

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);

    // ===============================================================
    // ===============================================================

    protected serviceRequest?: CpmCServiceRequestApiModel;
    /**
     * Subject to trigger statistics refresh in the general info component
     * when data changes in other tabs (tasks, files, object store)
     */
    protected refreshStatistics$ = new Subject<void>();
    /**
     * Gets the currently active tab from the URL query parameters.
     * Defaults to GENERAL tab if no valid tab is specified in the URL.
     *
     * @returns The current active tab
     */
    protected get currentTab(): EServiceRequestTab {
        const tab = this.activatedRoute.snapshot.queryParams['tab'];
        return Object.values(EServiceRequestTab).includes(tab as EServiceRequestTab)
            ? (tab as EServiceRequestTab)
            : EServiceRequestTab.GENERAL;
    }
    /**
     * Sets the active tab by updating the URL query parameters.
     * This maintains the tab state in the URL for bookmarking and navigation.
     *
     * @param value - The tab to activate
     */
    protected set currentTab(value: EServiceRequestTab) {
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {tab: value},
            queryParamsHandling: 'merge'
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    protected override async onReady(): Promise<void> {
        this.loadingStr = 'Loading service request...';

        await this.globalsService.sessionService.getRouteContextIsReadyPromise();
        this.serviceRequest = this.globalsService.sessionService.getServiceRequest();
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    protected override async onDestroy(): Promise<void> {
        // No cleanup required - using snapshot instead of subscriptions
    }

    // ===============================================================
    // ===============================================================

    /**
     * Opens the location modal in read-only mode
     */
    protected openLocationModal(): void {
        if (!this.serviceRequest?.location) return;
        this.globalsService.dialogService.open(
            NewEditLocationModalComponent,
            NewEditLocationModalComponent.getDialogServiceParameters({
                dialogOpenMode: EDialogOpenMode.READ_ONLY,
                extended: {
                    existing: this.serviceRequest.location
                }
            })
        );
    }
}
